import { useEffect, useState } from "react";
import { X, ChevronDown, ChevronUp, AlertCircle, CheckCircle, Info, XCircle, Film, Download } from "lucide-react";
import { getOfflineVideos, type OfflineVideoMetadata } from "@/utils/storage/offlineVideosStorage";
import { loadCheckpoint } from "@/utils/storage/downloadCheckpoints";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { generateHtmlReport } from "./VideoDownloadDebugger/generateHtmlReport";


// Typ dla logu
type LogLevel = "info" | "success" | "warning" | "error";

interface DownloadLog {
  id: string;
  timestamp: number;
  level: LogLevel;
  category: string;
  message: string;
  details?: Record<string, unknown>;
  videoId?: string;
}

// Global log store - będzie zbierać logi z całej aplikacji
export class VideoDownloadLogger {
  private static logs: DownloadLog[] = [];
  private static listeners: Array<(logs: DownloadLog[]) => void> = [];
  private static maxLogs = 200;

  static log(level: LogLevel, category: string, message: string, details?: Record<string, unknown>, videoId?: string) {
    const log: DownloadLog = {
      id: `${Date.now()}_${Math.random()}`,
      timestamp: Date.now(),
      level,
      category,
      message,
      details,
      videoId,
    };

    this.logs.unshift(log); // Najnowsze na górze
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    this.notifyListeners();
  }

  static subscribe(listener: (logs: DownloadLog[]) => void) {
    this.listeners.push(listener);
    listener(this.logs); // Wyślij aktualne logi od razu
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private static notifyListeners() {
    this.listeners.forEach(listener => listener([...this.logs]));
  }

  static clear() {
    this.logs = [];
    this.notifyListeners();
  }

  static getLogs() {
    return [...this.logs];
  }
}

// Skróty dla łatwiejszego użycia
export const videoLog = {
  info: (category: string, message: string, details?: Record<string, unknown>, videoId?: string) => 
    VideoDownloadLogger.log("info", category, message, details, videoId),
  success: (category: string, message: string, details?: Record<string, unknown>, videoId?: string) => 
    VideoDownloadLogger.log("success", category, message, details, videoId),
  warning: (category: string, message: string, details?: Record<string, unknown>, videoId?: string) => 
    VideoDownloadLogger.log("warning", category, message, details, videoId),
  error: (category: string, message: string, details?: Record<string, unknown>, videoId?: string) => 
    VideoDownloadLogger.log("error", category, message, details, videoId),
};

// Komponent debuggera
export const VideoDownloadDebugger = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<DownloadLog[]>([]);
  const [videos, setVideos] = useState<OfflineVideoMetadata[]>([]);
  const [selectedTab, setSelectedTab] = useState<"logs" | "status">("logs");
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [filterLevel, setFilterLevel] = useState<LogLevel | "all">("all");
  const [autoRefresh, setAutoRefresh] = useState(false); // ✅ Toggle for auto-refresh

  // Subscribe to logs
  useEffect(() => {
    const unsubscribe = VideoDownloadLogger.subscribe(setLogs);
    return unsubscribe;
  }, []);

  // Load videos status
  useEffect(() => {
    const loadVideos = async () => {
      const videosData = await getOfflineVideos();
      setVideos(Object.values(videosData));
    };
    
    void loadVideos(); // Load once on mount
    
    // Only set interval if autoRefresh is enabled
    if (autoRefresh) {
      const interval = setInterval(loadVideos, 2000); // Update co 2s
      return () => { clearInterval(interval); };
    }
    
    // Return undefined cleanup function when autoRefresh is off
    return undefined;
  }, [autoRefresh]); // ✅ Re-run when autoRefresh changes

  const toggleLog = (logId: string) => {
    setExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const getLevelIcon = (level: LogLevel) => {
    switch (level) {
      case "error": return <XCircle className="w-4 h-4 text-red-500" />;
      case "warning": return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case "success": return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case "error": return "bg-red-50 border-red-200";
      case "warning": return "bg-orange-50 border-orange-200";
      case "success": return "bg-green-50 border-green-200";
      default: return "bg-blue-50 border-blue-200";
    }
  };

  const filteredLogs = filterLevel === "all" 
    ? logs 
    : logs.filter(log => log.level === filterLevel);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString() + "." + date.getMilliseconds().toString().padStart(3, "0");
  };

  const downloadLogsAsJson = async () => {
    const dataToExport = {
      exportDate: new Date().toISOString(),
      platform: Capacitor.getPlatform(),
      totalLogs: logs.length,
      totalVideos: videos.length,
      logs: logs,
      videos: videos.map(v => ({
        ...v,
        checkpoint: null
      }))
    };

    const jsonString = JSON.stringify(dataToExport, null, 2);
    const fileName = `video-download-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;

    if (Capacitor.isNativePlatform()) {
      try {
        // Zapisz plik w cache directory
        const result = await Filesystem.writeFile({
          path: fileName,
          data: jsonString,
          directory: Directory.Cache,
          encoding: Encoding.UTF8
        });

        // Udostępnij plik przez Share API
        await Share.share({
          title: 'Video Download Logs',
          text: 'Logi pobierania video',
          url: result.uri,
          dialogTitle: 'Zapisz logi'
        });

        videoLog.success('Export', 'Logi wyeksportowane pomyślnie');
      } catch (error) {
        console.error('Error exporting logs:', error);
        videoLog.error('Export', 'Błąd podczas eksportu logów', { error });
      }
    } else {
      // Web version - existing code
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const downloadLogsAsHtml = async () => {
    const dataToExport = {
      exportDate: new Date().toISOString(),
      platform: Capacitor.getPlatform(),
      totalLogs: logs.length,
      totalVideos: videos.length,
      logs: logs,
      videos: videos
    };

    const htmlContent = generateHtmlReport(dataToExport);
    const fileName = `video-download-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.html`;

    if (Capacitor.isNativePlatform()) {
      try {
        const result = await Filesystem.writeFile({
          path: fileName,
          data: htmlContent,
          directory: Directory.Cache,
          encoding: Encoding.UTF8
        });

        await Share.share({
          title: 'Video Download Logs (HTML)',
          text: 'Raport logów pobierania video',
          url: result.uri,
          dialogTitle: 'Zapisz raport'
        });

        videoLog.success('Export', 'Raport HTML wyeksportowany pomyślnie');
      } catch (error) {
        console.error('Error exporting HTML logs:', error);
        videoLog.error('Export', 'Błąd podczas eksportu raportu HTML', { error });
      }
    } else {
      // Web version
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-0 right-[50px] z-50 bg-purple-600 text-white px-4 py-2 mt-safe-offset-0 rounded-full shadow-lg flex items-center gap-2 hover:bg-purple-700 opacity-50"
      >
        <Film />
        {logs.filter(l => l.level === "error").length > 0 && (
          <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
            {logs.filter(l => l.level === "error").length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-center">
      <div className="bg-white w-full md:w-[90%] md:max-w-4xl h-[80vh] md:h-[85vh] rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-purple-600 text-white p-4 rounded-t-2xl flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold">Video Download Debugger</h2>
            <p className="text-xs text-purple-200">
              {Capacitor.getPlatform()} • {videos.length} videos • {logs.length} logs
            </p>
          </div>
          <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-purple-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setSelectedTab("logs")}
            className={`flex-1 py-3 font-medium ${
              selectedTab === "logs"
                ? "border-b-2 border-purple-600 text-purple-600"
                : "text-gray-500"
            }`}
          >
            Logi ({logs.length})
          </button>
          <button
            onClick={() => setSelectedTab("status")}
            className={`flex-1 py-3 font-medium ${
              selectedTab === "status"
                ? "border-b-2 border-purple-600 text-purple-600"
                : "text-gray-500"
            }`}
          >
            Status ({videos.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {selectedTab === "logs" ? (
            <>
              {/* Filter */}
              <div className="mb-4 flex gap-2 flex-wrap">
                {(["all", "error", "warning", "success", "info"] as const).map(level => (
                  <button
                    key={level}
                    onClick={() => setFilterLevel(level)}
                    className={`px-3 py-1 rounded text-sm ${
                      filterLevel === level
                        ? "bg-purple-600 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                    {level !== "all" && (
                      <span className="ml-1">({logs.filter(l => l.level === level).length})</span>
                    )}
                  </button>
                ))}
                <button
                  onClick={downloadLogsAsJson}
                  className="ml-auto px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  JSON
                </button>
                <button
                  onClick={downloadLogsAsHtml}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  HTML
                </button>
                <button
                  onClick={() => VideoDownloadLogger.clear()}
                  className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm"
                >
                  Wyczyść logi
                </button>
              </div>

              {/* Logs list */}
              <div className="space-y-2">
                {filteredLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Brak logów
                  </div>
                ) : (
                  filteredLogs.map(log => (
                    <div
                      key={log.id}
                      className={`border rounded-lg p-3 ${getLevelColor(log.level)}`}
                    >
                      <div 
                        className="flex items-start gap-2 cursor-pointer"
                        onClick={() => toggleLog(log.id)}
                      >
                        {getLevelIcon(log.level)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-mono text-xs text-gray-600">
                              {formatTimestamp(log.timestamp)}
                            </span>
                            <span className="font-medium text-xs px-2 py-0.5 bg-white rounded">
                              {log.category}
                            </span>
                            {log.videoId && (
                              <span className="font-mono text-xs text-gray-600">
                                ID: {log.videoId}
                              </span>
                            )}
                          </div>
                          <p className="text-sm mt-1">{log.message}</p>
                        </div>
                        {log.details && (
                          expandedLogs.has(log.id) ? 
                            <ChevronUp className="w-4 h-4" /> : 
                            <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                      
                      {/* Expanded details */}
                      {expandedLogs.has(log.id) && log.details && (
                        <pre className="mt-2 p-2 bg-white rounded text-xs overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            /* Status Tab */
            <div className="space-y-3">
              {/* Auto-refresh toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Auto-refresh (2s)</span>
                  {autoRefresh && (
                    <span className="text-xs text-gray-500 animate-pulse">●</span>
                  )}
                </div>
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    autoRefresh
                      ? "bg-green-500 text-white"
                      : "bg-gray-300 text-gray-700"
                  }`}
                >
                  {autoRefresh ? "ON" : "OFF"}
                </button>
              </div>

              {videos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Brak pobranych video
                </div>
              ) : (
                videos.map(video => (
                  <VideoStatusCard key={video.videoId} video={video} />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Card ze statusem pojedynczego video
const VideoStatusCard = ({ video }: { video: OfflineVideoMetadata }) => {
  const [checkpoint, setCheckpoint] = useState<any>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadCheckpoint(video.videoId).then(setCheckpoint).catch(() => null);
  }, [video.videoId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "text-green-600 bg-green-100";
      case "downloading": return "text-blue-600 bg-blue-100";
      case "paused": return "text-orange-600 bg-orange-100";
      case "failed": return "text-red-600 bg-red-100";
      default: return "text-gray-600 bg-gray-100";
    }
  };

  return (
    <div className="border rounded-lg p-3 bg-white">
      <div 
        className="cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{video.videoName}</p>
            <p className="text-xs text-gray-500">{video.courseName}</p>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs px-2 py-1 rounded ${getStatusColor(video.status)}`}>
            {video.status}
          </span>
          <span className="text-xs text-gray-600">
            {video.downloadProgress.toFixed(1)}%
          </span>
          <span className="text-xs text-gray-600">
            {video.fileSizeMB} MB
          </span>
          <span className="text-xs text-gray-600">
            {video.selectedResolution}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-2">
          <DetailRow label="Video ID" value={video.videoId} />
          <DetailRow label="Source" value={video.videoSource} />
          <DetailRow label="Vimeo ID" value={video.vimeoId?.toString()} />
          <DetailRow label="File Path" value={video.localFilePath} mono />
          <DetailRow label="Download URL" value={video.downloadUrl} mono small />
          <DetailRow label="Started At" value={video.downloadStartedAt ? new Date(video.downloadStartedAt).toLocaleString() : "-"} />
          <DetailRow label="Completed At" value={video.downloadCompletedAt ? new Date(video.downloadCompletedAt).toLocaleString() : "-"} />
          
          {checkpoint && (
            <>
              <div className="font-medium text-xs mt-3 mb-1 text-purple-600">Checkpoint:</div>
              <DetailRow label="Downloaded" value={`${(checkpoint.downloadedBytes / 1024 / 1024).toFixed(2)} MB`} />
              <DetailRow label="Total Size" value={`${(checkpoint.totalSize / 1024 / 1024).toFixed(2)} MB`} />
              <DetailRow label="Progress" value={`${((checkpoint.downloadedBytes / checkpoint.totalSize) * 100).toFixed(1)}%`} />
              <DetailRow label="Last Update" value={new Date(checkpoint.lastUpdate).toLocaleString()} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, mono = false, small = false }: { label: string; value?: string; mono?: boolean; small?: boolean }) => {
  if (!value) return null;
  
  return (
    <div className="flex gap-2">
      <span className="text-xs text-gray-500 w-24 flex-shrink-0">{label}:</span>
      <span className={`text-xs flex-1 ${mono ? "font-mono" : ""} ${small ? "text-[10px]" : ""} break-all`}>
        {value}
      </span>
    </div>
  );
};

// Wrapper dla łatwego dodania do aplikacji
export const VideoDownloadDebugWrapper = ({ children }: { children: React.ReactNode }) => {
  const isDevelopmentMode = import.meta.env.VITE_BUILD_ENV === "development" || import.meta.env.VITE_BUILD_ENV === "staging";

  return (
    <>
      {children}
      {isDevelopmentMode && <VideoDownloadDebugger />}
    </>
  );
};
